import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ImAttachment } from "react-icons/im";
import { IoMdArrowForward } from "react-icons/io";
import { MdOutlineClose } from "react-icons/md";
import "./Chat.css";
import Tiles from "./Tiles";
import KgSidebar from "./KgSidebar";
import DocLinkSidebar from "./DocLinkSidebar";
import Composition from "../../components/Loancomposition/composition";
import { Centrifuge, Subscription } from "centrifuge";
import Emailmemo from "../../components/Emailmemo/emailmemo";
import Pdf from "../../components/Pdffile/pdf";
import { v4 as uuidv4 } from "uuid";
import ChatApiService, { getConversationThreadsByThreadIdApi } from "../../services/AxiosServices";
import RenderMessage from "../../components/message/RenderMessage";
import { useMyContext } from "../../context/DataContext";
import { GraphRenderer } from "../../components/GraphContainer/GraphRenderer";
import MessageFooter from "../../components/message/MessageFooter";
import DocTable from "../../components/DocumentTable/DocTable";
import { useAuth0 } from "@auth0/auth0-react";
import { getErrorMessage, logError } from "../../utils/errorHandling";


interface Message {
  user_id?: string;
  isDownloadable?: boolean;
  fileName?: string;
  text?: string;
  file?: File | null;
  fileType?: string | null;
  thread_ts?: string | null;
  senderType: string;
  component?: React.ReactNode;
  ask_action_confirmation?: boolean;
  is_result_interaction?: boolean;
  ask_result_interaction?: boolean;
  id?: string;
  assignedId?: string;
  keyPosition: number;
  visualizationData?: { [key: string]: any } | null;
  fileData: any;
  source: any;
  graph_data?: any;
  user_query?: string;
  page_numbers?: any;
}

let currentUsername: string | Blob = "";
let currentUUID: string | Blob = "";

const Chat: React.FC = () => {
  const { state, updateState } = useMyContext();
  useEffect(() => {
    const originalPosition = document.body.style.position;
    document.body.style.position = 'fixed';
    return () => {
      document.body.style.position = originalPosition;
    };
  }, []);

  let lastSender: string | null = null;
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [isRole, setIsRole] = useState<string>("user");
  const [adminResponse, setAdminResponse] = useState<any>(null);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [typingLoader, setTypingLoader] = useState<boolean>(false);
  const chatRef = useRef<HTMLTextAreaElement>(null);
  const [gaugeData, setGaugeData] = useState<any>({});
  const [zoomInBtn, setZoomInBtn] = useState<boolean>(false);
  const [hiddenAssignedIds, setHiddenAssignedIds] = useState(new Set());
  const [activeZoomInIds, setActiveZoomInIds] = useState(new Set());
  const [fileData, setFileData] = useState<any>({});
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.selectedDocument) {
      setSelectedFilename(location.state.selectedDocument);
      // Optional: clear the state so it doesn't persist on refresh, though it's fine either way
    }
  }, [location.state]);

  const [token, setToken] = useState<string | null>(null);
  const companyLogo = "/chaticonlogo.png";

  const { user, getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();

  const username = (user?.preferred_username) || "Moonjelly User";
  // const WS_URL = "wss://" + window.location.host + "/ws/";
  //const WS_URL = "wss://dev.moonjelly.nl/ws/";
  const WS_URL = "wss://" + window.location.host + "/connection/websocket"; //uat
  const AUTH_DOMAIN_URL = "https://" + window.location.host; //uat

  const getApiToken = async () => {
    return await getAccessTokenSilently({ authorizationParams: { audience: "auth-service" } });
  };

  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        console.log('User not authenticated');
        await loginWithRedirect().catch((error) => {
          logError("Chat auth redirect failed", error);
        });
        return;
      }

      try {
        const accessToken = await getApiToken();
        setToken(accessToken);
      } catch (error) {
        logError("Chat token retrieval failed", error);
      }
    };
    void fetchToken();
  }, [isAuthenticated]);

  const userSub = user?.sub ?? null;
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subRef = useRef<Subscription | null>(null);

  useEffect(() => {
    if (!token || !userSub || !currentUUID) return;

    const existingClient = centrifugeRef.current;
    const existingSubscription = subRef.current;
    const newChannelName = `$thread:${currentUUID}`;

    if (existingClient) {
      try {
        existingClient.disconnect();
        existingClient.removeAllListeners?.();
      } catch {
        console.error("Error disconnecting from previous centrifuge client");
      }
      centrifugeRef.current = null;
    }
    if (existingSubscription) {
      try {
        existingSubscription.unsubscribe();
        existingSubscription.removeAllListeners?.();
      } catch {
        console.error("Error unsubscribing from previous centrifuge channel");
      }
      subRef.current = null;
    }

    void (async () => {
      let clientId = "";

      try {
        const cf = new Centrifuge(WS_URL, { getToken: async () => await getApiToken() });

        cf.on("error", (ctx) => console.error("Centrifuge error", ctx));
        cf.on("connecting", () => console.log("centrifuge connecting"));
        cf.on("connected", (ctx) => {
          console.log("centrifuge connected");
          clientId = ctx.client ?? "";
        });
        cf.on("disconnected", () => console.log("centrifuge disconnected"));

        centrifugeRef.current = cf;
        cf.connect();
        await cf.ready();

        const fetchSubscriptionToken = async (subscriptionClientId: string) => {
          const accessToken = await getAccessTokenSilently({
            authorizationParams: { audience: "auth-service" }
          });

          const resp = await fetch(`${AUTH_DOMAIN_URL}/thread/${currentUUID}/subscribe-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ client: subscriptionClientId })
          });
          if (!resp.ok) {
            throw new Error(`subscribe-token request failed: ${resp.status}`);
          }
          const respJson = await resp.json();
          return respJson?.token;
        };

        const getSubscriptionToken = async () => {
          return await fetchSubscriptionToken(clientId);
        };

        const sub = cf.newSubscription(newChannelName, { getToken: getSubscriptionToken });

        sub.on("error", (ctx) => console.error("Centrifuge channel error", ctx));
        sub.on("subscribing", () => console.log("subscribing to centrifuge channel"));
        sub.on("subscribed", () => console.log("subscribed to centrifuge channel"));
        sub.on("unsubscribed", () => console.log("unsubscribed from centrifuge channel"));
        sub.on("publication", (ctx) => {
          try {
            renderData(ctx.data);
          } catch (e) {
            console.error("renderData failed", e);
          }
        });

        subRef.current = sub;
        sub.subscribe();
      } catch (error) {
        logError("Chat realtime setup failed", error);
        setTypingLoader(false);
        setMessages((prevMessages) => ([...prevMessages, {
          id: uuidv4(),
          text: `Realtime connection failed: ${getErrorMessage(error)}`,
          senderType: "admin",
          keyPosition: 0,
          fileData: {},
          source: "System",
        }]));
      }
    })();

    return () => {
      try {
        if (centrifugeRef.current) {
          centrifugeRef.current.disconnect();
          centrifugeRef.current.removeAllListeners?.();
        }
      } catch {
        console.error("Error disconnecting centrifuge during cleanup");
      }
      try {
        if (subRef.current) {
          subRef.current.unsubscribe();
          subRef.current.removeAllListeners?.();
        }
      } catch {
        console.error("Error unsubscribing from centrifuge during cleanup");
      }
      centrifugeRef.current = null;
      subRef.current = null;
    };

  }, [token, currentUUID, userSub, WS_URL])

  const renderData = (users) => {
    setTypingLoader(false);
    setIsRole("user");
    setAdminResponse(users);
    setFileData({ documentResult: users.document_result });
    if (users && users.visualization) {
      setGaugeData((prevState) => ({
        ...prevState,
        [users.visualization.query_type]: users.visualization.data,
      }));
      setMessages((prevMessages) => {
        const updatedMessages = prevMessages.map((msg) =>
          msg.senderType === "admin" && msg.text === ""
            ? {
              ...msg,
              visualizationData: {
                ...msg.visualizationData,
                [users.visualization.query_type]: users.visualization.data,
              },
            }
            : msg
        );
        return updatedMessages;
      });
    }
  };
  useEffect(() => {
    if (adminResponse) {
      let text = "";
      let source = adminResponse?.source_reference;
      const respMsg = adminResponse?.response_message;

      if (typeof respMsg === 'string') {
        text = respMsg;
      } else if (respMsg && typeof respMsg === 'object') {
        text = respMsg.answer || "";
        source = respMsg.source || source;
      }

      //@ts-ignore
      const newMessage: Message = {
        text: text,
        source: source,
        isDownloadable: adminResponse?.downloadable ? true : false,
        file: adminResponse?.downloadable
          ? adminResponse?.downloadable?.file
          : file || null,
        fileName: adminResponse?.downloadable
          ? adminResponse?.downloadable?.file_name
          : file || "",
        fileType: adminResponse?.downloadable
          ? adminResponse?.downloadable?.file_type
          : file?.type || null,
        senderType: "admin",
        thread_ts: adminResponse?.thread_ts,
        ask_action_confirmation: adminResponse?.ask_action_confirmation,
        is_result_interaction: adminResponse?.is_result_interaction,
        ask_result_interaction: adminResponse?.ask_result_interaction,
        user_id: username,
        user_query: adminResponse?.user_query,
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    }
  }, [adminResponse]);

  React.useEffect(() => {
    const newUuid = uuidv4();
    if (username !== currentUsername) {
      currentUsername = username;
      currentUUID = newUuid;
    }
  }, [username]);
  const adminReply = (component: ReactNode, visualizationData = null) => {
    setTimeout(() => {
      //@ts-ignore
      const adminMessage: Message = {
        text: "",
        file: null,
        fileType: null,
        senderType: "admin",
        component: component,
        visualizationData,
        fileData,
      };
      setMessages((prevMessages) => [...prevMessages, adminMessage]);
    }, 500);

    setIsRole("user");
  };
  const handleSendMessage = async (senderType: string) => {
    const actionId = uuidv4();
    setShowChat(true);
    setAdminResponse(null);

    if (newMessage.trim() || file || selectedFilename) {
      const capturedMessage = newMessage;
      setTypingLoader(true);
      setIsRole("user");
      //@ts-ignore
      const userMessage: Message = {
        id: actionId,
        text: capturedMessage || (selectedFilename ? selectedFilename : (file ? file.name : "")),
        file: file || null,
        fileName: selectedFilename ? selectedFilename : (file ? file.name : undefined),
        fileType: selectedFilename ? "PDF" : (file?.type || null),
        senderType,
        ask_action_confirmation: false,
        is_result_interaction: false,
      };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setNewMessage("");
      setFile(null);

      // Live API Call for Text Only
      const sendApiCall = async () => {
        try {
          const accessToken = await getApiToken();

          let textToSend = capturedMessage;
          let filenameToSend = selectedFilename;

          // If the message starts with the selected filename, extract the actual query
          if (filenameToSend && capturedMessage.startsWith(filenameToSend)) {
            textToSend = capturedMessage.substring(filenameToSend.length).trim();
          }

          const apiPayload: any = {
            user_id: username,
            text: textToSend || capturedMessage,
            channel_id: currentUUID as string,
            thread_ts: currentUUID as string,
            ts: actionId,
            chat_provider_name: "moonjelly"
          };

          let response;
          if (filenameToSend) {
            apiPayload.filename = filenameToSend;
            response = await ChatApiService.sendChatResponseBackByFilename(apiPayload, accessToken) as any;
          } else {
            response = await ChatApiService.sendChatResponseBackByData(apiPayload, accessToken) as any;
          }

          // Keep selected filename after sending until manually closed

          if (response) {
            const respMsg = response.response_message;
            let text = "";
            let source = response.source_reference || "";
            let graphData = response.graph_data || null;
            let documentResult = response.document_result || null;

            if (typeof respMsg === 'string') {
              text = respMsg;
            } else if (respMsg && typeof respMsg === 'object') {
              text = respMsg.answer || "";
              source = respMsg.source || source;
              graphData = respMsg.graph_data || graphData;
              documentResult = respMsg.document_result || documentResult;
            }

            // Fallback to filenameToSend for source if not provided by API
            if (!source && filenameToSend) {
              source = filenameToSend;
            }

            if (text) {
              const adminMessage: Message = {
                id: uuidv4(),
                text: text,
                senderType: "admin",
                keyPosition: 0,
                fileData: { documentResult },
                source: source || "Moonjelly AI",
                user_id: username,
                thread_ts: currentUUID as string,
                graph_data: graphData,
                user_query: response.user_query || textToSend || capturedMessage,
                page_numbers: response.page_numbers || (typeof respMsg === 'object' && respMsg !== null ? respMsg.page_numbers || respMsg.pages : null)
              };
              setMessages((prevMessages) => {
                // Simple deduplication: don't add if the last message is identical
                const lastMsg = prevMessages[prevMessages.length - 1];
                if (lastMsg && lastMsg.senderType === "admin" && lastMsg.text === text) {
                  return prevMessages;
                }
                return [...prevMessages, adminMessage];
              });
            }
          }
        } catch (error) {
          console.error("API Error:", error);
          const errorMessage: Message = {
            id: uuidv4(),
            text: "Something went wrong. Please try again.",
            senderType: "admin",
            keyPosition: 0,
            fileData: {},
            source: "System",
          };
          setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } finally {
          setTypingLoader(false);
        }
      };

      sendApiCall();

      // Existing local commands
      if (
        capturedMessage.trim().toLowerCase() === "what is their loan composition?" &&
        senderType === "user"
      ) {
        adminReply(<Composition />);
      }

      if (
        capturedMessage.trim().toLowerCase() ===
        "this looks good. create a investment memo for it" &&
        senderType === "user"
      ) {
        adminReply(<Pdf />);
      }
    }
  };
  const pdfUnselectHandler = () => {
    setFile(null);
  };
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const selectedFile = selectedFiles[0];
      setFile(selectedFile);
    }
    event.target.value = "";
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      handleAdminMessage();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleAdminMessage = () => {

  };
  const handleKeyPressSend = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(isRole);
    }
  };

  useEffect(() => {
    if (typingLoader === false) {
      if (chatRef.current) {
        chatRef.current.focus();
      }
    }
  }, [typingLoader]);

  const inputHandler = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (state.isKgSidebarOpen) {
      updateState("isKgSidebarOpen", false);
    }
    if (state.isDocLinkSidebarOpen) {
      updateState("isDocLinkSidebarOpen", false);
    }
  };

  const handleAddToChat = (docName: string) => {
    setSelectedFilename(docName);
    if (chatRef.current) {
      chatRef.current.focus();
    }
  };
  const zoomHandler = async (message, data) => {
    const { assignedId } = message;
    if (data === "interact") {
      setZoomInBtn(true);
      setActiveZoomInIds((prev) => {
        const updated = new Set(prev);
        updated.add(assignedId);
        return updated;
      });
      setHiddenAssignedIds((prev) => {
        const updated = new Set(prev);
        updated.delete(assignedId);
        return updated;
      });
    } else if (data === "stop_interact") {
      setZoomInBtn(false);
      setActiveZoomInIds((prev) => {
        const updated = new Set(prev);
        updated.delete(assignedId);
        return updated;
      });
      setHiddenAssignedIds((prev) => {
        const updated = new Set(prev);
        updated.add(assignedId);
        return updated;
      });
    }
    setTypingLoader(true);
    const formData = new FormData();
    formData.append("user_id", message.user_id);
    formData.append("text", "");
    formData.append("channel_id", message.thread_ts);
    formData.append("thread_ts", message.thread_ts);
    formData.append("button_value", data);
    formData.append("chat_provider_name", "INTERNAL_UI");
    formData.append("ts", "test");
    try {
      const chatResponse = async () => {
        const token = await getAccessTokenSilently();
        return (await ChatApiService.sendChatMessage(formData, token)) as any;
      };
      const resp = await chatResponse();
      if (resp) {
        console.log("got response");
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (chatRef.current) {
      autoExpand({ target: chatRef.current });
    }
  }, [newMessage]);

  const autoExpand = (event) => {
    const target = event.target;
    target.style.height = "auto";
    target.style.height = `${Math.max(40, Math.min(target.scrollHeight, 200))}px`;
  };
  const fetchConversationThreads = async (threadID: string) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await getConversationThreadsByThreadIdApi<any[]>(
        threadID, token
      );
      if (response) {
        const historyChat = response.map((item) => ({
          ask_action_confirmation: item.ask_action_confirmation,
          file: null,
          fileType: null,
          is_result_interaction: false,
          senderType: item.sender === "moonjelly" ? "admin" : "user",
          text: item.message,
          thread_ts: item.conversation_thread_id,
          is_action_confirmation: item.is_action_confirmation,
          source: item.source_document || "",
        }));
        //@ts-ignore
        setMessages(historyChat);
        setShowChat(true);
      } else {
        console.log("No conversation threads found.");

      }
    } catch (error) {
      console.error("Error fetching conversation threads:", error);
    }
  };

  useEffect(() => {
    if (state.chatHistorythreadId && state.chatHistorythreadId.length > 0) {
      void fetchConversationThreads(state.chatHistorythreadId);
    }
  }, [state.chatHistorythreadId]);

  useEffect(() => {
    return () => {
      updateState("chatHistorythreadId", "");
      updateState("isKgSidebarOpen", false);
      updateState("kgSidebarData", null);
      updateState("isDocLinkSidebarOpen", false);
      updateState("docLinkSidebarData", null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [previousHeight, setPreviousHeight] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }

    if (chatBoxRef.current) {
      const currentHeight = chatBoxRef.current.scrollHeight;
      if (currentHeight > previousHeight) {
        setPreviousHeight(currentHeight);
      }
    }
  }, [messages, previousHeight]);

  const filteredMsg = messages.filter((item) => item.text !== undefined);
  let previousId: string | null = null;
  const firstItem = filteredMsg[1] || {};
  const initialThreadTs = firstItem.thread_ts || null;
  const initialUserId = firstItem.user_id || null;

  const transformedData = filteredMsg.map((item) => {
    if (item.id) {
      previousId = item.id;
      return { ...item, id: item.id };
    }
    if (previousId) {
      return { ...item, id: previousId, assignedId: previousId };
    }
    return item;
  });

  let keyPosition = 0;
  let currentId: string | null | undefined = null;

  const dataKeyGet = transformedData.map((item, index, arr) => {
    const itemId = item.id || item.assignedId;

    if (itemId !== currentId) {
      currentId = itemId;
    }

    const groupItems = arr.filter((x) => (x.id || x.assignedId) === currentId);
    const hasAskResultInteraction = groupItems.some(
      (x) => x.ask_result_interaction === true
    );
    const hasIsResultInteraction = groupItems.some(
      (x) => x.is_result_interaction === true
    );
    const threadTs = initialThreadTs;
    const userId = initialUserId;

    if (
      item.senderType === "admin" &&
      (index === arr.length - 1 ||
        (arr[index + 1]?.id || arr[index + 1]?.assignedId) !== currentId)
    ) {
      return {
        ...item,
        id: item.id || previousId,
        keyPosition,
        ...(hasAskResultInteraction ? { ask_result_interaction: true } : {}),
        ...(hasIsResultInteraction ? { is_result_interaction: true } : {}),
        thread_ts: threadTs,
        user_id: userId,
      };
    }
    const {
      ask_result_interaction,
      is_result_interaction,
      thread_ts,
      user_id,
      ...rest
    } = item;
    return {
      ...rest,
      id: item.id || previousId,
      thread_ts: threadTs,
      user_id: userId,
    };
  });
  const handleAdminResponse = () => {
    if (adminResponse && adminResponse.visualization) {
      updateState("getData", adminResponse.visualization.query_type);
      adminReply(<></>, adminResponse.visualization);
    } else if (fileData !== undefined && adminResponse?.document_result) {
      adminReply(<></>);
    }
  };
  useEffect(() => {
    handleAdminResponse();
  }, [gaugeData, fileData, adminResponse]);
  return (
    <div className="chat-page-layout">
      <div
        className="chat-container"
        onClick={() => {
          if (state.isKgSidebarOpen) {
            updateState("isKgSidebarOpen", false);
          }
          if (state.isDocLinkSidebarOpen) {
            updateState("isDocLinkSidebarOpen", false);
          }
        }}
      >
        <div className={previousHeight > 400 ? "dynamic-chat-boxes" : "chat-box"} ref={chatBoxRef}>


          {/* {!showChat && <Tiles  showChat={setShowChat} message={newMessage} />} */}
          {!showChat && <Tiles />}

          {showChat && (
            <div className="chat-box-body">
              {dataKeyGet.length > 0 &&
                dataKeyGet.map((message, index) => {
                  const isAdmin = message.senderType === "admin";
                  const showProfilePic = isAdmin && lastSender !== "admin"; // Only show profile pic for the first admin message

                  // Update last sender
                  lastSender = isAdmin ? "admin" : "user";
                  const isLastMessage = index === dataKeyGet.length - 1;
                  return (
                    <div
                      key={index}
                      className={`chat-message ${message.senderType === "admin"
                        ? "admin-message"
                        : "user-message"
                        }`}
                    >
                      {showProfilePic ? (
                        <img
                          src={companyLogo}
                          alt="Admin Logo"
                          className="message-logo admin-logo"
                        />
                      ) : null}
                      <div
                        className={`message-text ${message.senderType === "admin" &&
                          !message.isDownloadable
                          ? "admin-bubble"
                          : "user-bubble"
                          } ${message.fileType === "application/pdf" || message.fileType === "text/plain" || message.fileType === "application/msword" || message.fileType === "text/csv" ||
                            message.fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                            message.fileType === "image/jpeg" || message.fileType === "image/jpeg" || message.fileType === "image/png"
                            || message.fileType === "application/vnd.ms-excel" || message.fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ? "file-bubble"
                            : ""
                          } ${!showProfilePic ? "space" : ""}`}
                      >
                        {message.senderType === "admin" && message.text === ""
                          ? (
                            <div>
                              <div className="messageDiv">
                                {message?.fileData?.documentResult && (
                                  <>
                                    <DocTable
                                      data={message?.fileData?.documentResult}
                                    />
                                  </>
                                )}
                                {message.visualizationData && message.visualizationData.query_type ? (
                                  <div className={
                                    message.visualizationData.query_type === "loan"
                                      ? "graph-loan-section"
                                      : message.visualizationData.query_type === "compare" ||
                                        message.visualizationData.query_type === "summary"
                                        ? "compare-summary-section"
                                        : ""
                                  }>
                                    <GraphRenderer
                                      message={message}
                                      data={message?.visualizationData?.data}
                                      queryData={
                                        message.visualizationData.query_type
                                      }
                                    />
                                  </div>

                                ) : null}
                                {message?.keyPosition === 0 ? (
                                  <MessageFooter message={message} isKgraph={true} />
                                ) : null}
                              </div>
                              <div style={{ display: "flex" }}>


                                {message.ask_result_interaction &&
                                  !message.is_result_interaction &&
                                  message.senderType === "admin" && isLastMessage &&
                                  !activeZoomInIds.has(message.assignedId) ? (
                                  <button
                                    className="zoomInButton"
                                    onClick={() => zoomHandler(message, "interact")}
                                  >
                                    Zoom In
                                  </button>
                                ) : null}
                                {message.is_result_interaction &&
                                  message.senderType === "admin" && isLastMessage &&
                                  !hiddenAssignedIds.has(message.assignedId) ? (
                                  <button
                                    className="zoomInButton zoomOutBtn"
                                    style={{ marginLeft: "4px" }}
                                    onClick={() =>
                                      zoomHandler(message, "stop_interact")
                                    }
                                  >
                                    Zoom Out
                                  </button>
                                ) : null}

                              </div>
                            </div>
                          )
                          : message.text && (
                            <p
                              className={
                                message.fileType === "application/pdf" || message.fileType === "text/plain" || message.fileType === "application/msword" || message.fileType === "text/csv" ||
                                  message.fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                                  message.fileType === "image/jpeg" || message.fileType === "image/jpeg" || message.fileType === "image/png"
                                  || message.fileType === "application/vnd.ms-excel" || message.fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                  ? "pdf"
                                  : ""
                              }
                            >
                              {message.ask_action_confirmation &&
                                message.senderType === "admin" ? (
                                <Emailmemo
                                  message={message}
                                  loader={setTypingLoader}
                                />
                              ) : (
                                <>
                                  <div className="messageDiv">
                                    <RenderMessage message={message as Message} />
                                    {
                                      message.keyPosition === 0 ? <MessageFooter message={message} isKgraph={true} />
                                        : null}
                                  </div>
                                  <div style={{ display: 'flex' }}>
                                    {message.ask_result_interaction &&
                                      !message.is_result_interaction &&
                                      message.senderType === "admin" && isLastMessage &&
                                      !activeZoomInIds.has(message.assignedId) ? (
                                      <button
                                        className="zoomInButton"
                                        onClick={() => zoomHandler(message, "interact")}
                                      >
                                        Zoom In
                                      </button>
                                    ) : null}
                                    {message.is_result_interaction &&
                                      message.senderType === "admin" && isLastMessage &&
                                      !hiddenAssignedIds.has(message.assignedId) ? (
                                      <button
                                        className="zoomInButton zoomOutBtn"
                                        style={{ marginLeft: "4px" }}
                                        onClick={() =>
                                          zoomHandler(message, "stop_interact")
                                        }
                                      >
                                        Zoom Out
                                      </button>
                                    ) : null}
                                  </div>
                                </>
                              )}
                            </p>
                          )}
                      </div>
                    </div>

                  );

                })}
              {typingLoader ? (
                <>
                  <div className="typing-indicator">
                    <img src="/chaticonlogo.png" alt="Logo" className="logo logomr" />
                  </div>
                </>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
          )}

        </div>
        <div className="chat-box-footer">
          <div className="input-field-container">
            <textarea
              value={newMessage}
              onChange={(e) => {
                inputHandler(e);
                autoExpand(e);
              }}
              onKeyDown={handleKeyPressSend}
              placeholder={selectedFilename ? "Chat with selected document" : "Type your response"}
              ref={chatRef}
              disabled={typingLoader}
              rows={1}
              className={zoomInBtn ? "text-area-chat" : ""}
            />
            <div className="input-actions">
              <button
                onClick={() => handleSendMessage(isRole)}
                className="send-button-inline"
                disabled={typingLoader || (!newMessage.trim() && !file && !selectedFilename) ? true : false}
                title="Send Message"
              >
                <IoMdArrowForward />
              </button>
              <button
                onClick={() => document.getElementById("file-input")?.click()}
                className="attach-button-inline"
                disabled={typingLoader ? true : false}
                title="Attach File"
              >
                <ImAttachment />
              </button>
            </div>
            {zoomInBtn ? <div className="zoom-input-icon"><img
              width={"13"}
              height={"13"}
              alt="avatar"
              style={{ height: '20px', width: '20px' }}
              src={"svg/lupe.png"}
            /></div> : null}
          </div>
          {file ? (
            <span className="selectedFileThumbnail">
              <span>{file.name}</span>
              <span onClick={pdfUnselectHandler}>
                <MdOutlineClose />
              </span>
            </span>
          ) : null}
          {selectedFilename ? (
            <span className="selectedFileThumbnail">
              <span>{selectedFilename}</span>
              <span onClick={() => setSelectedFilename(null)}>
                <MdOutlineClose />
              </span>
            </span>
          ) : null}
          <input
            id="file-input"
            type="file"
            style={{ display: "none" }}
            onChange={(e) => handleFileSelection(e)}
            accept=".pdf,.docx,.doc,.txt,.csv,.jpeg,.jpg,.png,.xls,.xlsx"
          />
        </div>
      </div>
      <KgSidebar
        isOpen={state.isKgSidebarOpen}
        onClose={() => updateState("isKgSidebarOpen", false)}
        documents={state.kgSidebarData || []}
        onAddToChat={handleAddToChat}
      />
      <DocLinkSidebar
        isOpen={state.isDocLinkSidebarOpen}
        onClose={() => updateState("isDocLinkSidebarOpen", false)}
        documents={state.docLinkSidebarData || []}
        onAddToChat={handleAddToChat}
      />
    </div>
  );
};

export default Chat;